import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Button, Typography, 
    CircularProgress, Modal, IconButton,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    FormControl, Select, MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import apiService, { viewGcpDocument } from '../../services/actions';

function CCBulkUpload() {
    const loginObjStr = localStorage.getItem('loginObj');
    const loginObj = JSON.parse(loginObjStr);
    const userCode = loginObj?.userSession?.userCode || null;
    
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [base64Data, setBase64Data] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    
    const [uploadsList, setUploadsList] = useState([]);
    
    // Enhanced Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        fetchUploadsData(1, pageSize);
    }, []);

    const fetchUploadsData = async (pageNo = 1, size = pageSize) => {
        setLoading(true);
        try {
            // Create payload with pagination parameters
            const payload = {
                pageNumber: pageNo,
                pageSize: size
            };

            const response = await apiService.getCCBulkUploads(payload);
            
            if (response.data?.successList) {
                setUploadsList(response.data.successList);
                setCurrentPage(pageNo);
                setTotalElements(response.data.totalElements || response.data.successList.length);
                setTotalPages(response.data.totalPages || Math.ceil((response.data.totalElements || response.data.successList.length) / size));
            } else {
                setUploadsList([]);
                setTotalElements(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Failed to fetch uploads data:', error);
            toast.error('Failed to load uploads data');
            setUploadsList([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    // Handle page size change
    const handlePageSizeChange = (event) => {
        const newSize = event.target.value;
        setPageSize(newSize);
        fetchUploadsData(1, newSize); // Reset to first page when changing page size
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedFile(null);
        setFileName('');
        setBase64Data('');
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const validExtensions = new Set(['.xlsx', '.xls', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']);
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const fileType = file.type;
        
        if (!validExtensions.has(`.${fileExtension}`) && !validExtensions.has(fileType)) {
            toast.error('Please select a valid Excel file (.xlsx or .xls)');
            return;
        }

        // Check file size (e.g., 10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setSelectedFile(file);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            setBase64Data(base64);
        };
        reader.onerror = () => {
            toast.error('Failed to read file');
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile || !base64Data) {
            toast.error('Please select a file first');
            return;
        }

        const payload = {
            "orderExcelDoc": base64Data,
            "type": "xlsx",
            "createdBy": userCode || "thanishwaran",
            "modifiedBy": userCode || "thanishwaran"
        };

        setUploadLoading(true);
        try {
            const response = await apiService.CCbulkupload(payload);
            
            if (response.status === 200 || response.data?.success) {
                toast.success('File uploaded successfully!');
                handleCloseModal();
                fetchUploadsData(1, pageSize); // Refresh and go to first page
            } else {
                const errorMessage = response.data?.message || 'Upload failed';
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            const errorMessage = error.response?.data?.message || 'Upload failed. Please try again.';
            toast.error(errorMessage);
        } finally {
            setUploadLoading(false);
        }
    };

    const handleViewClick = (rec) => {
        if (!rec?.ccExcel) {
            toast.error("File not available.");
            return;
        }
        viewGcpDocument(rec.ccExcel, (err) => toast.error(err));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        
        const date = new Date(dateString);
        
        // FIX: Use Number.isNaN instead of isNaN
        if (Number.isNaN(date.getTime())) {
            return '-';
        }
        
        return date.toLocaleDateString('en-IN') + ' ' + date.toLocaleTimeString('en-IN', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    // Helper function to create page button
    const createPageButton = (pageNumber, isCurrent = false) => {
        return (
            <Button
                key={pageNumber}
                variant={isCurrent ? "contained" : "outlined"}
                onClick={() => fetchUploadsData(pageNumber, pageSize)}
                sx={{
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: isCurrent ? 600 : 400,
                    backgroundColor: isCurrent ? '#004c97' : 'white',
                    border: isCurrent ? 'none' : '1px solid #d1d5db',
                    color: isCurrent ? 'white' : '#374151',
                    '&:hover': {
                        backgroundColor: isCurrent ? '#003366' : '#f3f4f6'
                    }
                }}
            >
                {pageNumber}
            </Button>
        );
    };

    // Helper function to render ellipsis
    const renderEllipsis = (key) => {
        return (
            <Typography key={key} sx={{ px: 1, color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                ...
            </Typography>
        );
    };

    // FIX: Refactored renderPageButtons to reduce cognitive complexity
    const renderPageButtons = () => {
        if (totalPages <= 1) {
            return null;
        }

        const pages = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        // First page and ellipsis
        if (startPage > 1) {
            pages.push(createPageButton(1));
            
            if (startPage > 2) {
                pages.push(renderEllipsis("ellipsis1"));
            }
        }
        
        // Page numbers
        for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
            pages.push(createPageButton(pageNumber, currentPage === pageNumber));
        }
        
        // Last page and ellipsis
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(renderEllipsis("ellipsis2"));
            }
            
            pages.push(createPageButton(totalPages));
        }
        
        return pages;
    };
   
    return (
        <Box sx={{ p: 3, backgroundColor: '#F8FBFF', borderRadius: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    CC Bulk Upload
                </Typography>
                  <Box display="flex" gap={2}>
       
                    <Button 
                        variant="contained" 
                        startIcon={<CloudUploadIcon />}
                        onClick={handleOpenModal}
                        sx={{
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#1565c0',
                            },
                            fontWeight: 'bold',
                            textTransform: 'none',
                        }}
                    >
                        Upload Excel
                    </Button>
                </Box>
             
            </Box>

            {loading && (
                <Box
                    position="fixed"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    bgcolor="rgba(0, 0, 0, 0.1)"
                    zIndex={999}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    <CircularProgress color="secondary" />
                </Box>
            )}

            {/* Uploads Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', minHeight: 350, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead sx={{ bgcolor: '#f4f6f9', '& .MuiTableCell-root': { bgcolor: '#f4f6f9', fontWeight: 'bold' } }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f9' }}>File Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f9' }}>Created Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f9' }}>Modified Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f9' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f9' }}>View</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {uploadsList.map((rec, index) => (
                                <TableRow key={rec.id || index} hover>
                                    <TableCell>{rec.ccExcel || rec.fileName || '-'}</TableCell>
                                    <TableCell>{formatDate(rec.createdDate)}</TableCell>
                                    <TableCell>{formatDate(rec.modifiedDate)}</TableCell>
                                    <TableCell>{rec.createdBy || '-'}</TableCell>
                                    <TableCell>
                                        <IconButton 
                                            onClick={() => handleViewClick(rec)}
                                            disabled={!rec.ccExcel}
                                            color="primary"
                                            size="small"
                                        >
                                            <Visibility />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {uploadsList.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="body1" sx={{ py: 3, color: 'text.secondary' }}>
                                            No uploads found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Enhanced Pagination Controls */}
                {totalElements > 0 && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2} px={2}>
                        {/* Page Info */}
                        <Typography variant="body2" color="text.secondary">
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalElements)} of {totalElements} entries
                        </Typography>

                        {/* Pagination Controls */}
                        <Box display="flex" alignItems="center" gap={2}>
                            {/* Page Size Dropdown */}
                            <FormControl size="small" sx={{ minWidth: 80 }}>
                                <Select
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                    displayEmpty
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Page Navigation */}
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Button
                                    variant="outlined"
                                    onClick={() => fetchUploadsData(currentPage - 1, pageSize)}
                                    disabled={currentPage === 1}
                                    sx={{
                                        minWidth: '80px',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: 'white',
                                        color: currentPage === 1 ? '#9ca3af' : '#374151',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        '&:disabled': {
                                            backgroundColor: '#f9fafb',
                                            color: '#9ca3af'
                                        },
                                        '&:hover': {
                                            backgroundColor: '#f3f4f6',
                                            borderColor: '#9ca3af'
                                        }
                                    }}
                                >
                                    Previous
                                </Button>

                                {/* Page Numbers */}
                                {renderPageButtons()}

                                <Button
                                    variant="outlined"
                                    onClick={() => fetchUploadsData(currentPage + 1, pageSize)}
                                    disabled={currentPage >= totalPages}
                                    sx={{
                                        minWidth: '80px',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: 'white',
                                        color: currentPage >= totalPages ? '#9ca3af' : '#374151',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        '&:disabled': {
                                            backgroundColor: '#f9fafb',
                                            color: '#9ca3af'
                                        },
                                        '&:hover': {
                                            backgroundColor: '#f3f4f6',
                                            borderColor: '#9ca3af'
                                        }
                                    }}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Paper>

            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="upload-modal-title"
                aria-describedby="upload-modal-description"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography id="upload-modal-title" variant="h6" component="h2">
                            Upload Excel File
                        </Typography>
                        <IconButton onClick={handleCloseModal} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                        <input
                            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            style={{ display: 'none' }}
                            id="excel-file-upload"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="excel-file-upload">
                            <Button 
                                variant="outlined" 
                                component="span"
                                fullWidth
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    py: 2,
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    borderColor: '#1976d2',
                                    color: '#1976d2',
                                    '&:hover': {
                                        borderColor: '#1565c0',
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    }
                                }}
                            >
                                Choose Excel File
                            </Button>
                        </label>
                        
                        {fileName && (
                            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'success.main', fontWeight: 'medium' }}>
                                Selected: {fileName}
                            </Typography>
                        )}
                        
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'text.secondary' }}>
                            Supported formats: .xlsx, .xls (Max: 10MB)
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button 
                            onClick={handleCloseModal}
                            variant="outlined"
                            disabled={uploadLoading}
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUpload}
                            variant="contained"
                            disabled={!selectedFile || uploadLoading}
                            startIcon={uploadLoading ? <CircularProgress size={16} /> : null}
                            sx={{ textTransform: 'none' }}
                        >
                            {uploadLoading ? 'Uploading...' : 'Upload File'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

export default CCBulkUpload;